// Sharing (M43): a "Send this game to a friend" row on every game page (shared/_share_bar) and "Share my result" on
// the finish card (games/_finish), with a picture score card. Plain buttons with words on them: Email, Text message,
// Facebook, Copy, plus the phone's own Share sheet where there is one. The email, text and Facebook buttons are real
// links, so the page's row works without this script. Every link carries ?via=<channel>, which lib/acquisition.ts
// files as a "shared" first visit, so the return-visit report counts who came from a share. Nothing is sent to
// Facebook until the player chooses it: it's a link, not a script.
import { flush, track } from "./analytics"

export type Channel = "native" | "email" | "text" | "facebook" | "copy" | "picture"

export interface Result {
  name: string // "Spider Solitaire"
  what: string // "a game of Spider Solitaire", "a hard Sudoku puzzle"
  verb: string // "won", "solved", "finished"
  time?: string // "4:12", when the timer is on
  moves?: string // "87"
  score?: string // "8 of 10", for a quiz
  grid?: string // a spoiler-free line of the result, "✅✅❌✅" (no questions, no answers)
  same?: boolean // the link opens the same game (a trivia round), so the friend can try to beat it
  daily?: string // "October 12", for a daily
  deal?: string // FreeCell's deal number
}

/** A link with ?via=<channel>, added to any query the link already has (?deal=). */
export function tagged(url: string, via: string): string {
  const u = new URL(url)
  u.searchParams.set("via", via)
  return u.toString()
}

/** The words of a shared result: what was played, how it went, and an invitation. No spoilers: no cards, no answers. */
export function resultText(r: Result): string {
  const what = r.daily ? `the daily ${r.name} for ${r.daily}` : r.deal ? `${r.name} deal #${r.deal}` : r.what
  const how = r.score ? ` with ${r.score} right` : [r.time && ` in ${r.time}`, r.moves && ` with ${r.moves} moves`].filter(Boolean).join("")
  const same = r.daily || r.deal || r.same
  return `I ${r.verb} ${what}${how}.${r.grid ? `\n${r.grid}\n` : " "}${same ? "Can you beat that? Play the same one free:" : "It's free to play, with no sign-up:"}`
}

/** The subject line of an email. */
export function resultSubject(r: Result): string {
  return r.daily || r.deal || r.same ? `Can you beat my ${r.name}?` : `My ${r.name} game`
}

/** Points every channel's link in `root` at this text and address. */
export function fill(root: HTMLElement, text: string, url: string, subject: string): void {
  const enc = encodeURIComponent
  root.dataset.shareText = text
  root.dataset.shareUrl = url
  root.dataset.shareSubject = subject
  const link = (via: Channel, href: string): void => {
    const a = root.querySelector<HTMLAnchorElement>(`a[data-share-channel="${via}"]`)
    if (a) a.href = href
  }
  link("email", `mailto:?subject=${enc(subject)}&body=${enc(`${text}\n${tagged(url, "email")}`)}`)
  link("text", `sms:?&body=${enc(`${text} ${tagged(url, "text")}`)}`)
  link("facebook", `https://www.facebook.com/sharer/sharer.php?u=${enc(tagged(url, "facebook"))}`)
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Older Safari, or clipboard access refused: a selected, off-screen text box and the old copy command.
    const box = document.createElement("textarea")
    box.value = text
    box.setAttribute("readonly", "")
    box.style.cssText = "position:fixed;left:-9999px;top:0"
    document.body.append(box)
    box.select()
    let ok = false
    try { ok = document.execCommand("copy") } catch { /* reported below */ }
    box.remove()
    return ok
  }
}

/** Wires one share row: native Share, Copy and Save picture buttons, and counting every channel. `picture` gives the
 *  score card, when the row has one. */
export function wire(root: HTMLElement, picture?: () => File | null): void {
  const said = root.querySelector<HTMLElement>("[data-share-said]")
  const say = (s: string): void => { if (said) said.textContent = s }
  const native = root.querySelector<HTMLElement>('[data-share-channel="native"]')
  if (native && typeof navigator.share === "function") native.hidden = false
  root.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-share-channel]")
    if (!el || !root.contains(el)) return
    const channel = el.dataset.shareChannel as Channel
    const text = root.dataset.shareText ?? ""
    const url = root.dataset.shareUrl ?? location.href
    track("share", { channel, place: root.dataset.sharePlace ?? "page" })
    flush()
    if (channel === "native") {
      const file = picture?.() ?? null
      const data: ShareData = { text: `${text} ${tagged(url, "share")}` }
      if (file && navigator.canShare?.({ files: [file] })) data.files = [file]
      navigator.share(data).catch(() => { /* closed without sharing */ })
    } else if (channel === "copy") {
      void copy(`${text} ${tagged(url, "link")}`).then((ok) => say(ok
        ? "Copied. Paste it into a message or an email."
        : "Your browser wouldn't copy it. Use Email or Text message instead."))
    } else if (channel === "picture") {
      say("Saved. Look for it in your downloads or photos.")
    }
  })
}

// ---- The score card: a 1200×630 picture, the game's preview art on the left and the result on the right ----------

export interface Card { title: string; sub: string; big: string; small: string; foot: string }

/** The words on the score card. */
export function cardWords(r: Result, host: string): Card {
  const done = r.verb.charAt(0).toUpperCase() + r.verb.slice(1)
  return {
    title: r.name,
    sub: r.daily ? `Daily, ${r.daily}` : r.deal ? `Deal #${r.deal}` : "",
    big: r.score ? `${r.score} right` : r.time ? `${done} in ${r.time}` : `${done}!`,
    small: r.score ? (r.time ? `in ${r.time}` : "") : r.moves ? `${r.moves} moves` : "",
    foot: r.daily || r.deal || r.same ? `Can you beat it? ${host}` : `Play free at ${host}`,
  }
}

/** Draws the card; resolves to a PNG, or null where the browser can't draw one. */
export async function drawCard(c: Card, art: string): Promise<Blob | null> {
  const canvas = document.createElement("canvas")
  canvas.width = 1200
  canvas.height = 630
  const g = canvas.getContext("2d")
  if (!g) return null
  g.fillStyle = "#fffdf6"
  g.fillRect(0, 0, 1200, 630)
  g.fillStyle = "#1d4a38"
  g.fillRect(0, 0, 520, 630)
  const img = new Image()
  img.src = art
  try {
    await img.decode()
    g.drawImage(img, 0, 0, 520, 630, 0, 0, 520, 630)
  } catch { /* the plain green half is fine */ }
  const font = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'
  const fit = (text: string, weight: number, size: number, max: number): void => {
    let s = size
    do { g.font = `${weight} ${s}px ${font}`; s -= 2 } while (g.measureText(text).width > max && s > 20)
  }
  const x = 576, max = 580
  g.textBaseline = "alphabetic"
  g.fillStyle = "#1d4a38"
  g.font = `700 28px ${font}`
  g.fillText("Card & Puzzle Games", x, 120)
  g.fillStyle = "#1f2a24"
  fit(c.title, 800, 64, max)
  g.fillText(c.title, x, 210)
  g.fillStyle = "#3d4a43"
  g.font = `400 32px ${font}`
  if (c.sub) g.fillText(c.sub, x, 262)
  g.fillStyle = "#1d4a38"
  fit(c.big, 800, 80, max)
  g.fillText(c.big, x, 380)
  g.fillStyle = "#3d4a43"
  g.font = `400 40px ${font}`
  if (c.small) g.fillText(c.small, x, 440)
  g.fillStyle = "#1d4a38"
  fit(c.foot, 700, 30, max)
  g.fillText(c.foot, x, 540)
  return new Promise((done) => canvas.toBlob(done, "image/png"))
}

/** The page's own share row (shared/_share_bar), filled by the server; this adds Share, Copy and counting. */
export function initShareBars(): void {
  document.querySelectorAll<HTMLElement>("[data-share-bar]").forEach((root) => wire(root))
}
